/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListIdentityAccessRequest,
  ListIdentityAccessSecurity,
} from "../models/operations/listidentityaccess.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildIdentityAccessQuery,
  IdentityAccessQueryData,
  prefetchIdentityAccess,
  queryKeyIdentityAccess,
} from "./identityAccess.core.js";
export {
  buildIdentityAccessQuery,
  type IdentityAccessQueryData,
  prefetchIdentityAccess,
  queryKeyIdentityAccess,
};

export type IdentityAccessQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listIdentityAccess access
 *
 * @remarks
 * List the MCP servers and skills an identity is authorized to reach, through grants on the user or on any role they hold, less any blocking grant that withdraws the same scope. Authorization only: plugin membership decides what a resource is distributed through, not who may use it, so it does not widen this list.
 */
export function useIdentityAccess(
  request: ListIdentityAccessRequest,
  security?: ListIdentityAccessSecurity | undefined,
  options?: QueryHookOptions<IdentityAccessQueryData, IdentityAccessQueryError>,
): UseQueryResult<IdentityAccessQueryData, IdentityAccessQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildIdentityAccessQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listIdentityAccess access
 *
 * @remarks
 * List the MCP servers and skills an identity is authorized to reach, through grants on the user or on any role they hold, less any blocking grant that withdraws the same scope. Authorization only: plugin membership decides what a resource is distributed through, not who may use it, so it does not widen this list.
 */
export function useIdentityAccessSuspense(
  request: ListIdentityAccessRequest,
  security?: ListIdentityAccessSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    IdentityAccessQueryData,
    IdentityAccessQueryError
  >,
): UseSuspenseQueryResult<IdentityAccessQueryData, IdentityAccessQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildIdentityAccessQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setIdentityAccessData(
  client: QueryClient,
  queryKeyBase: [
    parameters: { userId: string; gramSession?: string | undefined },
  ],
  data: IdentityAccessQueryData,
): IdentityAccessQueryData | undefined {
  const key = queryKeyIdentityAccess(...queryKeyBase);

  return client.setQueryData<IdentityAccessQueryData>(key, data);
}

export function invalidateIdentityAccess(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: { userId: string; gramSession?: string | undefined }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "access", "listIdentityAccess", ...queryKeyBase],
  });
}

export function invalidateAllIdentityAccess(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "access", "listIdentityAccess"],
  });
}
